import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastVariant } from '../../../../components/Toast/toast.model';
import { toastService } from '../../../../components/Toast/toast.service';
import { firestore } from '../../../../firebase/firebase';
import { FirestoreCollection } from '../../../../firebase/firebase.models';
import { useAuth } from '../../../Auth/AuthContext';

const { Text } = Typography;

interface EmailInfoProps {
  email: string;
}

export const EmailInfo: React.FC = () => {
  const [componentDisabled, setComponentDisabled] = useState<boolean>(true);
  const { currentUser, updateEmail, sendVerificationEmail } = useAuth();
  const { t } = useTranslation();

  const [email, setEmail] = useState<string>(currentUser.email);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  function onFinish(value: EmailInfoProps): void {
    updateEmail(value.email)
      .then(() => {
        toastService.show(t('CONFIRM_EMAIL_SUCCESS'), t('SUCCESS'));
        sendVerificationEmail()
          .then((response) => {
            if (response.status === 'SUCCESS') {
              toastService.show(t('FEATURES.AUTH.SIGN_UP.VERIFICATION_EMAIL_SENT'), t('CONFIRM_EMAIL'), {
                variant: ToastVariant.SUCCESS,
              });

              const docRef = firestore.collection(FirestoreCollection.USERS).doc(currentUser.uid);
              docRef
                .update(value)
                .then(() => {
                  toastService.show(t('FEATURES.AUTH.PROFILE_UPDATED'), t('SUCCESS'), {
                    variant: ToastVariant.SUCCESS,
                  });
                })
                .catch((e) => {
                  toastService.show(t(e.message || t('UPDATE_PROFILE_ERROR')), t('ERROR'), {
                    variant: ToastVariant.ERROR,
                  });
                  onReset(); // Reset w przypadku błędu aktualizacji
                });
            } else {
              toastService.show(t(response.message), t('ERROR'), { variant: ToastVariant.ERROR });
              onReset(); // Reset w przypadku błędu weryfikacji e-maila
            }
            // show verification email modal
          })
          .catch((e) => {
            toastService.show(t(e.message || t('ERROR_UNEXPECTED')), t('ERROR'), { variant: ToastVariant.ERROR });
            onReset(); // Reset w przypadku błędu weryfikacji e-maila
          });
      })
      .catch((e) => {
        toastService.show(e.message, t('ERROR'), {
          variant: ToastVariant.ERROR,
        });
        onReset();
      });
    setComponentDisabled(!componentDisabled);
  }
  function onReset(): void {
    form.resetFields();
    setEmail(currentUser.email);
  }
  useEffect(() => {
    form.resetFields();
  }, [email]);

  return (
    <div className="space-y-4">
      {!currentUser.emailVerified && (
        <Alert
          message={t('CONFIRM_EMAIL')}
          description={t('ACCESS_DENIED')}
          type="warning"
          showIcon
          className="text-sm"
          action={
            <Button
              type="primary"
              size="small"
              style={{ backgroundColor: 'orange', color: 'white' }}
              onClick={() => {
                setLoading(true);
                sendVerificationEmail()
                  .then((response) => {
                    if (response.status === 'SUCCESS') {
                      toastService.show(t('FEATURES.AUTH.SIGN_UP.VERIFICATION_EMAIL_SENT'), t('CONFIRM_EMAIL'), {
                        variant: ToastVariant.SUCCESS,
                      });
                    } else {
                      toastService.show(t(response.message), t('ERROR'), { variant: ToastVariant.ERROR });
                    }
                  })
                  .catch((e) => {
                    toastService.show(t(e.message || t('ERROR_UNEXPECTED')), t('ERROR'), {
                      variant: ToastVariant.ERROR,
                    });
                  })
                  .finally(() => setLoading(false));
              }}
              loading={loading}>
              {t('CONFIRM')}
            </Button>
          }
        />
      )}

      <Card className="shadow-sm border border-gray-200 rounded-lg" styles={{ body: { padding: '16px' } }}>
        <div className="space-y-3">
          <Text className="text-gray-700 text-sm font-medium">{t('EMAIL_INFORMATION') || 'Informacje o email'}</Text>

          <Form
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            initialValues={{ email: email }}
            autoComplete="off"
            disabled={componentDisabled}
            onFinish={onFinish}
            form={form}
            size="small">
            <Form.Item
              label={<Text className="text-sm">Email</Text>}
              name="email"
              className="!mb-3"
              rules={[{ required: true, message: t('FIELD_REQUIRED') }]}>
              <Input className="text-sm" />
            </Form.Item>

            <Form.Item wrapperCol={{ offset: 6, span: 18 }} hidden={componentDisabled} className="!mb-0">
              <div className="space-x-2">
                <Button type="primary" htmlType="submit" size="small">
                  {t('SAVE')}
                </Button>
                <Button htmlType="button" onClick={onReset} size="small">
                  {t('RESET')}
                </Button>
              </div>
            </Form.Item>
          </Form>

          {/* Przycisk zmiany email ukryty - powoduje komplikacje */}
          {componentDisabled && false && (
            <div className="flex justify-end">
              <Button size="small" onClick={() => setComponentDisabled(!componentDisabled)}>
                Zmień email
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
